import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { toast } from 'sonner';

import { isLockedVisit } from '@/components/blocks/berth-plan/berth-plan.utils';
import { DataTable } from '@/components/table/data-table';
import { EditableCell } from '@/routes/(app)/(scenarios)/(create)/components/editable-cell';
import { getCellEditError } from '@/routes/(app)/(scenarios)/(create)/components/shipment-planning.utils';
import { useShipmentTableColumns } from '@/routes/(app)/(scenarios)/(create)/hooks/use-shipment-table-columns';
import {
  CellValue,
  ShipVisit,
} from '@/routes/(app)/(scenarios)/(create)/types';

interface ShipmentPlanningTableProps {
  shipVisits: Array<ShipVisit>;
  cutoffMs?: number;
  onUpdateVisit: (visitId: string, patch: Partial<ShipVisit>) => void;
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
}

const ShipmentPlanningTable: FC<ShipmentPlanningTableProps> = ({
  shipVisits,
  cutoffMs,
  onUpdateVisit,
  rowSelection,
  onRowSelectionChange,
}) => {
  const { t } = useTranslation();
  const [selectedCell, setSelectedCell] = useState<{
    rowId: string;
    columnId: string;
  } | null>(null);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'arrival_time', desc: false },
  ]);
  const columns = useShipmentTableColumns();

  const defaultColumn = useMemo<Partial<ColumnDef<ShipVisit, CellValue>>>(
    () => ({
      cell: EditableCell,
    }),
    []
  );

  const table = useReactTable<ShipVisit>({
    data: shipVisits,
    columns,
    defaultColumn,
    getRowId: (row) => row.id,
    state: { sorting, rowSelection },
    onSortingChange: setSorting,
    onRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    enableRowSelection: (row) => !isLockedVisit(row.original, cutoffMs),
    meta: {
      selectedCell,
      setSelectedCell,
      isRowLocked: (visit) => isLockedVisit(visit, cutoffMs),
      updateData: (rowId, columnId, value) => {
        const edited = shipVisits.find((visit) => visit.id === rowId);
        if (!edited || isLockedVisit(edited, cutoffMs)) return;
        const error = getCellEditError(edited, columnId, value, cutoffMs);
        if (error) {
          toast.error(t(`scenarios.create.customizePlanning.${error}`));
          return;
        }
        onUpdateVisit(rowId, { [columnId]: value });
      },
    },
  });

  return <DataTable table={table} />;
};

export default ShipmentPlanningTable;
